import React, {FC, useMemo} from 'react';
import classNames from 'classnames';
import {Col} from 'components/Grid';
import {Typography} from 'components/Typography';
import Picture from 'components/Picture';
import Link, {LINK_THEME} from 'components/Link';
import CustomRoutingLink from 'components/CustomRoutingLink';
import {CatalogMenuCategoriesList} from 'modules/categories/components/CatalogMenuCategoriesList';
import {IBanner, ICategoryListItem, ICategorySection} from 'modules/categories/types';
import {splitTextWithBr} from 'core/helpers/string';
import style from './style.module.scss';
import {NEXT_PUBLIC_MASTER_HOST, NEXT_PUBLIC_WINTER_DESIGN} from 'core/helpers/env';

const BANNER_THEMES = {
    champagne: 'green',
    glass: 'blue',
    strong: 'yellow',
    water: 'blue',
    wine: 'default',
} as const;

type TBannerThemeKey = keyof typeof BANNER_THEMES;

interface ICatalogMenuSectionCategoriesProps {
    activeCode: ICategorySection['code'];
    banners?: IBanner[];
    categories: ICategoryListItem[];
    section?: string;
    sectionLink?: string;
    presentLink?: string;
}

const isBeautifulWordsSection = (code: string) => 'beautiful-words' === code;

const BannerItem: React.FC<{
    banner: IBanner;
    activeCode: string;
    isLast: boolean;
    sectionLink?: string;
    section?: string;
}> = ({banner, activeCode, isLast, sectionLink, section}) => {
    const {img, title, code, background, className} = banner;
    const isBeautifulWords = isBeautifulWordsSection(activeCode);

    return (
        <React.Fragment key={code}>
            <div
                className={classNames(style.categoryBanner, style.imgBanner, {
                    [style[BANNER_THEMES[activeCode as TBannerThemeKey]]]: !background,
                    [style.bw]: isBeautifulWords,
                    [style[className as string]]: 'string' === typeof className,
                })}
                style={background ? {background} : {}}
            >
                <CustomRoutingLink className={style.categoryBannerWrapper} href={code}>
                    <div className={style.categoryBannerTitle}>
                        <Typography element={'div'} variant={'h5'}>
                            {splitTextWithBr(title)}
                        </Typography>
                    </div>
                    <Picture sources={{}} src={img} decoding={'async'} />
                </CustomRoutingLink>
            </div>
            {!isBeautifulWords && isLast && (
                <div className={classNames(style.categoryBanner, style[BANNER_THEMES[activeCode as TBannerThemeKey]])}>
                    <Link href={sectionLink} theme={LINK_THEME.black}>
                        <div className={style.categoryBannerTitle}>
                            <Typography variant={'h5'}>{section || 'Все товары'}</Typography>
                        </div>
                    </Link>
                </div>
            )}
        </React.Fragment>
    );
};

const CategoryItem: React.FC<{item: ICategoryListItem; isSub?: boolean}> = ({item, isSub}) => {
    const {categoryType, list, categoriesColumn} = item;
    const hasSubCategories = categoriesColumn?.length;

    if (hasSubCategories) {
        return <div>{renderCategoriesList(categoriesColumn, true)}</div>;
    }

    return (
        <div className={classNames({[style.categoriesListContainer]: isSub})}>
            <Typography element={'div'} variant={'label-16-m'}>
                {categoryType}
            </Typography>
            {list && <CatalogMenuCategoriesList list={list} />}
        </div>
    );
};

const renderCategoriesList = (categories: ICategoryListItem[], isSub?: boolean) => {
    return categories.map((item, index) => <CategoryItem key={index} item={item} isSub={isSub} />);
};

const PresentBanner: React.FC<{presentLink: string}> = ({presentLink}) => (
    <Link href={presentLink}>
        <div
            className={classNames(style.categoryStaticBanner, style.categoryBannerPresents, {
                // eslint-disable-next-line @typescript-eslint/naming-convention
                'category-banner-presents-winter-design': NEXT_PUBLIC_WINTER_DESIGN,
            })}
        >
            <Typography color={'white100'} variant={'h5'}>
                В подарочной упаковке
            </Typography>
            <Picture
                alt={'banner'}
                sources={{}}
                src={
                    NEXT_PUBLIC_WINTER_DESIGN
                        ? '/images/winter/present-winter.webp'
                        : '/images/category-banners/present.webp'
                }
            />
        </div>
    </Link>
);

const DiscountBanner: React.FC = () => (
    <Link href={`${NEXT_PUBLIC_MASTER_HOST}/stock/discount/`}>
        <div
            // NEW_YEAR_2026
            className={classNames(style.categoryStaticBanner, style.categoryBannerDiscount, {
                // eslint-disable-next-line @typescript-eslint/naming-convention
                'category-banner-discount-winter-design': NEXT_PUBLIC_WINTER_DESIGN,
            })}
        >
            <Typography color={'white100'} variant={'h3-custom'}>
                Все акции <br />
                SimpleWine
            </Typography>
            <Picture
                sources={{}}
                src={
                    NEXT_PUBLIC_WINTER_DESIGN
                        ? '/images/winter/discount-banner-winter.webp'
                        : '/images/category-banners/discount-banner.webp'
                }
            />
        </div>
    </Link>
);

export const CatalogMenuSectionCategories: FC<ICatalogMenuSectionCategoriesProps> = ({
    categories,
    banners,
    section,
    activeCode,
    sectionLink,
    presentLink,
}) => {
    const isBeautifulWords = isBeautifulWordsSection(activeCode);

    const renderBanners = useMemo(
        () => (
            <Col isNoOffset={true}>
                <div className={classNames(style.bannerCategories, {[style.bw]: isBeautifulWords})}>
                    {banners?.map((banner, index) => (
                        <BannerItem
                            key={banner.code}
                            banner={banner}
                            activeCode={activeCode}
                            isLast={index === banners.length - 1}
                            sectionLink={sectionLink}
                            section={section}
                        />
                    ))}
                </div>
            </Col>
        ),
        [isBeautifulWords, banners, activeCode, sectionLink, section],
    );

    return (
        <div>
            <div className={style.categoriesRow}>{renderBanners}</div>
            <div className={style.categoriesRow}>
                <Col md={12} lg={!isBeautifulWords ? 8 : 12} isNoOffset={true}>
                    <div className={style.categories}>{renderCategoriesList(categories)}</div>
                </Col>
                <div className={style.bannersCol}>
                    {presentLink && <PresentBanner presentLink={presentLink} />}
                    {!isBeautifulWords && <DiscountBanner />}
                </div>
            </div>
        </div>
    );
};
